const DEFAULT_TIMEOUT = 10000; // 10 second default timeout

const api = {
  async request(endpoint, options = {}) {
    const timeout = options.timeout || DEFAULT_TIMEOUT;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(endpoint, {
        ...options,
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        let details = null;
        try {
          details = await response.json();
        } catch (_) {
          // A non-JSON proxy error still gets the status-based fallback below.
        }
        const error = new Error(
          details?.error || `API request failed: ${response.statusText}`
        );
        error.status = response.status;
        error.field = details?.field;
        error.currentRevision = details?.currentRevision;
        throw error;
      }
      // Handle cases where there might not be a JSON body
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json();
      }
      return response.text();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw error;
    }
  },

  async login(username, password) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout for login
    
    try {
      const res = await fetch('/api/v1/admin/login', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      clearTimeout(timeoutId);
      
      if (res.ok) {
        return { success: true };
      } else if (res.status === 401) {
        return { success: false, error: 'Invalid credentials' };
      } else if (res.status === 429) {
        return { success: false, error: 'Too many attempts. Please wait a minute.' };
      } else {
        return { success: false, error: `Server error (${res.status})` };
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request timed out. Please try again.' };
      }
      // Network error or CORS failure
      console.error('Login error:', error);
      return { success: false, error: 'Connection failed. Please check your network.' };
    }
  },

  async logout() {
    await fetch('/api/v1/admin/logout', { method: 'POST', credentials: 'include' });
  },

  // Generic GET
  get(endpoint) { return this.request(endpoint); },
  
  // Admin IDs
  getAdminIds() {
    return this.get('/api/v1/admin/ids');
  },
  
  addAdminId(id) {
    return this.request('/api/v1/admin/ids', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
  },
  
  removeAdminId(id) {
    return this.request(`/api/v1/admin/ids/${id}`, {
      method: 'DELETE',
    });
  },
  
  deleteAdminData() {
    return this.request('/api/v1/admin/data', {
      method: 'DELETE',
    });
  },

  // Analytics Data
  getStats(adminOnly = false) {
    return this.get(`/api/v1/analytics/stats?adminOnly=${adminOnly}`);
  },

  getWorkouts(params = {}) {
    const queryParams = new URLSearchParams();
    
    // Add all parameters to query string
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    
    return this.get(`/api/v1/analytics/workouts?${queryParams.toString()}`);
  },

  // Exercise Analytics
  getExerciseStats(adminOnly = false) {
    return this.get(`/api/v1/analytics/exercises?adminOnly=${adminOnly}`);
  },

  // Delete individual workout
  deleteWorkout(sessionID) {
    return this.request(`/api/v1/analytics/workout/${sessionID}`, {
      method: 'DELETE',
    });
  },

  // Food Search Analytics
  getFoodSearchStats(adminOnly = false) {
    return this.get(`/api/v1/analytics/food-searches?adminOnly=${adminOnly}`);
  },

  // Food Log Analytics
  getFoodLogStats(adminOnly = false) {
    return this.get(`/api/v1/analytics/food-log/stats?adminOnly=${adminOnly}`);
  },

  // Service Health
  getFoodSearchHealth() {
    return this.get('/api/v1/health/food-search');
  },

  // Database Update Dates
  getDatabaseUpdateDates() {
    return this.get('/api/v1/database/update-dates');
  },

  // Notice APIs
  getNotice() {
    return this.get('/api/v1/notice');
  },
  setNotice(message, title) {
    const payload = { message };
    if (title && title.trim()) payload.title = title.trim();
    return this.request('/api/v1/admin/notice', { method: 'POST', body: JSON.stringify(payload) });
  },
  clearNotice() {
    return this.request('/api/v1/admin/notice', { method: 'DELETE' });
  },

  // App release configuration APIs
  getAppReleaseConfig() {
    return this.get('/api/v1/admin/app-releases');
  },
  updateAppReleaseConfig(configuration) {
    return this.request('/api/v1/admin/app-releases', {
      method: 'PUT',
      body: JSON.stringify(configuration),
    });
  },
  remindOutdatedUsersAgain() {
    return this.request('/api/v1/admin/app-releases/remind', {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    });
  },

  // Remote feature flag APIs
  getFeatureFlags() {
    return this.get('/api/v1/admin/feature-flags');
  },
  updateFeatureFlag(key, configuration, expectedRevision) {
    return this.request(`/api/v1/admin/feature-flags/${key}`, {
      method: 'PUT',
      body: JSON.stringify({
        expectedRevision,
        production: configuration.production,
        testFlight: configuration.testFlight,
      }),
    });
  },

  // Product Configuration APIs
  getProductConfig() {
    return this.get('/api/v1/admin/products');
  },
  updateProductConfig(products, version) {
    return this.request('/api/v1/admin/products', {
      method: 'PUT',
      body: JSON.stringify({ products, version }),
    });
  },

  // App Store Connect APIs
  getAppStoreSummary() {
    return this.get('/api/v1/appstore/summary');
  },
  getAppStoreDownloads(period = 'month') {
    return this.get(`/api/v1/appstore/downloads?period=${period}`);
  },
  getAppStoreRevenue(period = 'month') {
    return this.get(`/api/v1/appstore/revenue?period=${period}`);
  },
  getAppStoreReviews(unrepliedOnly = false) {
    return this.get(`/api/v1/appstore/reviews?unreplied=${unrepliedOnly}`);
  },
};

export default api;
